import { Injectable, inject } from '@angular/core';

import { PartnerType } from '@tgx/shared/enums';
import { ProfileCache, ProfileUpdateInput, ProfileWhereUniqueInput } from '@tgx/shared/interfaces';
import { CacheService, ProfilesService, URL_WEB_BFF } from '@tgx/shared/services';
import { SharedLoggingService } from '@tgx/shared/logging';
import axios from 'axios';
import { WEB_BFF_ENDPOINTS } from '@tgx/shared/constants';

@Injectable({
  providedIn: 'root',
})
export class OnboardingProgessService {
  private cacheService = inject(CacheService);
  private profilesService = inject(ProfilesService);
  private loggingService = inject(SharedLoggingService);

  profile: ProfileCache;

  private urlWebBff = inject(URL_WEB_BFF);

  async setStartDate(orgCode: string, partnerType: PartnerType): Promise<void> {
    const profile = (await this.cacheService.retrieveProfilesByCodes([orgCode]))[0];

    let onboardingField: 'buyerOnboardingStart' | 'sellerOnboardingStart';
    if (partnerType === PartnerType.BUYER) {
      onboardingField = 'buyerOnboardingStart';
    } else if (partnerType === PartnerType.SELLER) {
      onboardingField = 'sellerOnboardingStart';
    } else {
      return;
    }

    if (!profile[onboardingField]) {
      profile[onboardingField] = new Date();
      const data: ProfileUpdateInput = { [onboardingField]: profile[onboardingField].toISOString() };
      const where: ProfileWhereUniqueInput = { ownerCode: orgCode };

      // Update the profile with the new onboarding start date.
      await this.profilesService.updateProfile(data, where);
      // Force the server-side cache to reload so the new data is available everywhere.
      await this.forceServerCacheUpdate();
      // Refresh the local cache to ensure the latest profile data is loaded.
      await this.cacheService.refreshProfiles();
    }
  }

  // Only for Buyers
  async setBuyerEndDate(orgCode: string): Promise<void> {
    const profile = (await this.cacheService.retrieveProfilesByCodes([orgCode]))[0];

    if (!profile.buyerOnboardingEnd) {
      profile.buyerOnboardingEnd = new Date();
      const data: ProfileUpdateInput = { buyerOnboardingEnd: profile.buyerOnboardingEnd.toISOString() };
      const where: ProfileWhereUniqueInput = { ownerCode: orgCode };

      // Update the profile with the new onboarding start date.
      await this.profilesService.updateProfile(data, where);
      // Force the server-side cache to reload so the new data is available everywhere.
      await this.forceServerCacheUpdate();
      // Refresh the local cache to ensure the latest profile data is loaded.
      await this.cacheService.refreshProfiles();
    }
  }

  private async forceServerCacheUpdate() {
    try {
      await axios.post(this.urlWebBff + WEB_BFF_ENDPOINTS.FORCE_RELOAD_CACHE);
    } catch (error) {
      if (!(error as { response?: unknown } | null)?.response) {
        this.loggingService.recordError('Error forcing onboarding cache reload', {
          file: 'onboarding-progess.service.ts',
          method: 'forceServerCacheUpdate',
          description: error instanceof Error ? error.message : String(error),
        });
      }
      throw error;
    }
  }
}
